import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/supabase";

export default function WaitlistForm({ eventId, ticketTypeId, ticketTypeName }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error("Vink de voorwaarden aan om verder te gaan");
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/waitlist`, {
        event_id: eventId,
        ticket_type_id: ticketTypeId,
        email: email.trim(),
        first_name: firstName.trim() || null,
        terms_accepted: true,
      });
      setDone(true);
      toast.success("Je staat op de wachtlijst");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Ging niet lukken");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`waitlist-success-${ticketTypeId}`}
        className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-100"
      >
        <div className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-4 w-4" /> Je staat op de wachtlijst
        </div>
        <p className="mt-1 text-emerald-100/80 text-xs">
          Zodra iemand annuleert voor <b>{ticketTypeName}</b>, sturen we je een mail met een koop-link.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`waitlist-form-${ticketTypeId}`}
      className="mt-3 rounded-xl border border-svj-cyan/30 bg-svj-cyan/5 p-4 space-y-3"
    >
      <div className="flex items-start gap-2">
        <Bell className="h-4 w-4 text-svj-cyan mt-0.5" />
        <div className="text-xs text-slate-200 leading-relaxed">
          <b>Uitverkocht</b> — maar er komt regelmatig iets vrij door annuleringen. Meld je aan
          voor de wachtlijst voor <b>{ticketTypeName}</b>.
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Voornaam (optioneel)"
          data-testid={`waitlist-first-name-${ticketTypeId}`}
          className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan text-sm"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mailadres"
          data-testid={`waitlist-email-${ticketTypeId}`}
          className="h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan text-sm"
        />
      </div>
      <label className="flex items-start gap-2 text-[11px] text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          required
          data-testid={`waitlist-terms-${ticketTypeId}`}
          className="mt-0.5 h-3.5 w-3.5 rounded border-svj-border bg-svj-bg text-svj-cyan focus:ring-svj-cyan accent-svj-cyan"
        />
        <span>
          Ik ga akkoord met de{" "}
          <Link to="/voorwaarden" target="_blank" rel="noopener noreferrer" className="text-svj-cyan hover:underline">
            voorwaarden
          </Link>{" "}
          en het{" "}
          <Link to="/privacybeleid" target="_blank" rel="noopener noreferrer" className="text-svj-cyan hover:underline">
            privacybeleid
          </Link>{" "}
          en dat ik één mail per vrijgekomen ticket krijg.
        </span>
      </label>
      <button
        type="submit"
        disabled={submitting || !terms}
        data-testid={`waitlist-submit-${ticketTypeId}`}
        className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-2 px-4 text-sm font-bold transition-all ${
          submitting || !terms
            ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
            : "bg-svj-cyan/20 border border-svj-cyan/50 text-svj-cyan hover:bg-svj-cyan/30"
        }`}
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
        {submitting ? "Bezig…" : "Zet me op de wachtlijst"}
      </button>
    </form>
  );
}
